import React, { useEffect, useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { FiPlus, FiTrash2, FiSearch } from "react-icons/fi";
import { toast } from "react-toastify";
import AcessoModal from "./AcessoModal";
import RemoverAcessoModal from "./RemoverAcessoModal";
import { CheckRelatorioAcesso } from "../../../../data/services";
import { getNomeAmigavel } from "./relatorios";

const ITEMS_PER_PAGE = 8;

const RelatoriosAcesso = () => {
  const [acessos, setAcessos] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchAcessos = async () => {
    try {
      const res = await CheckRelatorioAcesso();
      setAcessos(res);
      setFiltered(res);
    } catch {
      toast.error("Erro ao carregar acessos.");
    }
  };

  useEffect(() => {
    fetchAcessos();
  }, []);

  // Filtragem dinâmica
  useEffect(() => {
    const result = acessos.filter((a) => {
      const matricula = a.LOGIN.toLowerCase();
      const relatorios = a.relatorios
        .map((r) => getNomeAmigavel(r).toLowerCase())
        .join(" ");
      return (
        matricula.includes(search.toLowerCase()) ||
        relatorios.includes(search.toLowerCase())
      );
    });
    setFiltered(result);
    setPage(1);
  }, [search, acessos]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const currentPageData = useMemo(
    () =>
      filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [filtered, page]
  );

  const handleRemoveClick = (user) => {
    setSelectedUser(user);
    setShowRemoveModal(true);
  };

  return (
    <Container>
      <Header>
        <div>
          <Title>📋 Gestão de Acessos — Relatórios</Title>
          <Subtitle>
            Configure quem pode visualizar cada relatório analítico do sistema.
          </Subtitle>
        </div>
        <AddButton onClick={() => setShowModal(true)}>
          <FiPlus size={18} />
          <span>Cadastrar Acesso</span>
        </AddButton>
      </Header>

      <SearchBar>
        <FiSearch size={18} />
        <input
          type="text"
          placeholder="Buscar por matrícula ou relatório..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </SearchBar>

      <TableContainer>
        <StyledTable>
          <thead>
            <tr>
              <th>Matrícula</th>
              <th>Relatórios com Acesso</th>
              <th style={{ textAlign: "center" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {currentPageData.length === 0 ? (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: "1rem" }}>
                  Nenhum resultado encontrado.
                </td>
              </tr>
            ) : (
              currentPageData.map((item) => (
                <tr key={item.LOGIN}>
                  <td>{item.LOGIN}</td>
                  <td>
                    {item.relatorios.map((r, i) => (
                      <Tag key={i}>{getNomeAmigavel(r)}</Tag>
                    ))}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <RemoveButton onClick={() => handleRemoveClick(item)}>
                      <FiTrash2 size={16} />
                      Remover
                    </RemoveButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </StyledTable>
      </TableContainer>

      {/* Paginação */}
      {totalPages > 1 && (
        <Pagination>
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>
            Anterior
          </button>
          <span>
            Página {page} de {totalPages}
          </span>
          <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>
            Próxima
          </button>
        </Pagination>
      )}

      {showModal && (
        <AcessoModal onClose={() => setShowModal(false)} onSuccess={fetchAcessos} />
      )}

      {showRemoveModal && selectedUser && (
        <RemoverAcessoModal
          login={selectedUser.LOGIN}
          relatorios={selectedUser.relatorios}
          onClose={() => setShowRemoveModal(false)}
          onSuccess={fetchAcessos}
        />
      )}
    </Container>
  );
};

export default RelatoriosAcesso;

//
// 🎨 Styled Components
//
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  padding: 2rem;
  background: #f7f8f9;
  min-height: 100vh;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 1.9rem;
  font-weight: 700;
  color: #222;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #555;
  margin-top: 0.3rem;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #8fd19e, #a8e6a1);
  color: #0f5132;
  padding: 0.7rem 1.4rem;
  border: none;
  border-radius: 10px;
  font-size: 1.2rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(140, 205, 150, 0.3);
  transition: all 0.2s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(120, 195, 130, 0.4);
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: white;
  border: 1px solid #e4e4e4;
  border-radius: 10px;
  padding: 0.6rem 1rem;
  margin-bottom: 1rem;
  input {
    border: none;
    outline: none;
    width: 100%;
    font-size: 1.2rem;
  }
  svg {
    color: #777;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  th {
    text-align: left;
    background: #fafafa;
    color: #555;
    padding: 14px 18px;
    font-size: 0.95rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  td {
    padding: 14px 18px;
    font-size: 1.2rem;
    border-top: 1px solid #eee;
  }
  tr:hover {
    background: #f5f7fa;
  }
`;

const Tag = styled.span`
  background: #e8f5e9;
  color: #256029;
  padding: 5px 10px;
  margin: 3px;
  border-radius: 10px;
  font-size: 1.2rem;
  display: inline-block;
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  color: #e74c3c;
  border: none;
  font-size: 1.1rem;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    color: #c0392b;
    transform: scale(1.05);
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  gap: 1rem;
  button {
    background: #f0f0f0;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
`;
